import {LensScope} from './types';

/** Every Lens scope, in the order the draft form lists them. */
export const LENS_SCOPES: LensScope[] = [
  'lens:read',
  'lens:notes:write',
  'lens:properties:write',
  'lens:policies:write',
  'lens:alerts:write',
  'lens:agent:run',
];

export const LENS_SCOPE_COPY: Record<LensScope, {label: string; description: string}> = {
  'lens:read': {
    label: 'Read Lens data',
    description: 'Portfolios, properties, imagery, notes, reports and lookouts.',
  },
  'lens:notes:write': {
    label: 'Write notes',
    description: 'Create and edit notes, including saving detections as notes.',
  },
  'lens:properties:write': {
    label: 'Change portfolios and properties',
    description:
      'Create portfolios and properties, edit properties, build reports and share links.',
  },
  'lens:policies:write': {
    label: 'Manage lookouts',
    description: 'Create, edit and archive lookouts, and enroll properties in them.',
  },
  'lens:alerts:write': {
    label: 'Dismiss lookout alerts',
    description: 'Dismiss alerts that lookouts raise.',
  },
  'lens:agent:run': {
    label: 'Run analyses',
    description: 'Start property analyses, run plans and object detection. Uses agent-run quota.',
  },
};

// The scope each Lens MCP tool requires beyond `lens:read`, as the tools in
// backend/mcp_servers/lens/tools check it with `require_scope`.
const SCOPE_OF_TOOL: Record<string, LensScope> = {
  create_note: 'lens:notes:write',
  update_note: 'lens:notes:write',
  save_detections_as_notes: 'lens:notes:write',
  create_portfolio: 'lens:properties:write',
  create_property: 'lens:properties:write',
  update_property: 'lens:properties:write',
  update_properties: 'lens:properties:write',
  create_tag: 'lens:properties:write',
  update_tag: 'lens:properties:write',
  delete_tag: 'lens:properties:write',
  create_report: 'lens:properties:write',
  export_report_pdfs: 'lens:properties:write',
  create_public_share_link: 'lens:properties:write',
  delete_public_share_link: 'lens:properties:write',
  create_lookout_policy: 'lens:policies:write',
  update_lookout_policy: 'lens:policies:write',
  archive_lookout_policy: 'lens:policies:write',
  enroll_lookout_properties: 'lens:policies:write',
  unenroll_lookout_properties: 'lens:policies:write',
  dismiss_lookout_alert: 'lens:alerts:write',
  run_property_analysis: 'lens:agent:run',
  create_run_plan: 'lens:agent:run',
  start_run_plan: 'lens:agent:run',
  detect_objects: 'lens:agent:run',
};

/** The scopes a run needs to repeat calls to these tools, named without their `tool_lens_` prefix. */
export function scopesForTools(toolNames: Iterable<string>): LensScope[] {
  const needed = new Set<LensScope>(['lens:read']);
  for (const name of toolNames) {
    const scope = SCOPE_OF_TOOL[name];
    if (scope) needed.add(scope);
  }
  return LENS_SCOPES.filter((scope) => needed.has(scope));
}
